import React, { useState, useCallback, forwardRef } from "react";
import { TextField, TextFieldProps } from "@mui/material";
import { formatDecimal, parseDecimal } from "@/utils/decimalInput";

export interface DecimalTextFieldProps
  extends Omit<TextFieldProps, "value" | "onChange" | "type"> {
  /** Valeur numérique (affichée avec virgule quand le champ n'est pas focalisé). */
  value: number | undefined | null;
  /** Appelé avec le nombre parsé (ou emptyValue si chaîne vide et emptyValue défini). */
  onChange: (value: number | undefined | null) => void;
  /** Si true, un champ vide envoie emptyValue à onChange (défaut undefined). Sinon envoie 0. */
  optional?: boolean;
  /** Quand optional est true, valeur envoyée lorsque l'utilisateur vide le champ (défaut: undefined). Mettre null si le formulaire attend null. */
  emptyValue?: undefined | null;
}

/**
 * Champ décimal : affichage avec virgule, touche "." du pavé numérique convertie en ",".
 * Pendant la saisie, la chaîne est conservée localement pour éviter que "1," ne redevienne "1".
 */
export const DecimalTextField = forwardRef<HTMLInputElement, DecimalTextFieldProps>(
  function DecimalTextField(
    {
      value,
      onChange,
      optional,
      emptyValue,
      inputProps,
      onFocus,
      onBlur,
      inputRef: inputRefProp,
      ...rest
    },
    ref
  ) {
    const [isFocused, setIsFocused] = useState(false);
    const [editStr, setEditStr] = useState("");

    const displayValue = isFocused ? editStr : (value != null ? formatDecimal(value) : "");

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setIsFocused(true);
        setEditStr(value != null ? formatDecimal(value) : "");
        onFocus?.(e);
      },
      [value, onFocus]
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setIsFocused(false);
        const raw = editStr.trim();
        if (raw === "") {
          onChange(optional ? (emptyValue ?? undefined) : 0);
        } else {
          onChange(parseDecimal(editStr));
        }
        onBlur?.(e);
      },
      [editStr, optional, emptyValue, onChange, onBlur]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const v = e.target.value.replace(".", ",");
        setEditStr(v);
        const raw = v.trim();
        onChange(raw === "" ? (optional ? (emptyValue ?? undefined) : 0) : parseDecimal(v));
      },
      [onChange, optional, emptyValue]
    );

    const mergeRefs = useCallback(
      (el: HTMLInputElement | null) => {
        if (typeof ref === "function") ref(el);
        else if (ref) ref.current = el;
        if (typeof inputRefProp === "function") inputRefProp(el);
        else if (inputRefProp) (inputRefProp as React.MutableRefObject<HTMLInputElement | null>).current = el;
      },
      [ref, inputRefProp]
    );

    return (
      <TextField
        {...rest}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        inputRef={mergeRefs}
        inputProps={inputProps}
      />
    );
  }
);
